/**
 * Trusted time (privacy requirement PR-07).
 *
 * Expiry must not be defeatable by winding the device clock back. Without this,
 * "unreadable the moment it expires" is untrue on a device its owner controls.
 *
 * The approach: remember the latest server time we have seen, anchored to a
 * monotonic reading that the user cannot rewind. If the wall clock ever reads
 * earlier than that anchor implies, treat the clock as untrustworthy and fall
 * back to the monotonic estimate — never to the user's number.
 */

export interface ClockState {
  /** Latest server instant observed, epoch ms. */
  lastKnownGoodMs: number;
  /** Monotonic reading taken at the same moment. */
  monotonicAtSampleMs: number;
}

export interface ClockSources {
  wallClockMs(): number;
  /** Must never decrease within a session. */
  monotonicMs(): number;
}

export class TrustedClock {
  #state: ClockState | null;
  #sources: ClockSources;
  #tampered = false;

  constructor(sources: ClockSources, state: ClockState | null = null) {
    this.#sources = sources;
    this.#state = state;
  }

  /** Call with the `serverTime` on every API response. */
  observeServerTime(serverMs: number): void {
    const monotonic = this.#sources.monotonicMs();
    if (!this.#state || serverMs > this.floor()) {
      this.#state = { lastKnownGoodMs: serverMs, monotonicAtSampleMs: monotonic };
      this.#tampered = false;
    }
  }

  /** The earliest instant it can honestly be, derived from the trusted anchor. */
  floor(): number {
    if (!this.#state) return 0;
    const elapsed = Math.max(0, this.#sources.monotonicMs() - this.#state.monotonicAtSampleMs);
    return this.#state.lastKnownGoodMs + elapsed;
  }

  /**
   * Best estimate of now. Takes the wall clock when it is consistent with the
   * anchor, and the anchor when the wall clock has been moved backwards.
   */
  now(): number {
    const wall = this.#sources.wallClockMs();
    const floor = this.floor();
    if (this.#state && wall < floor - 5_000) {
      this.#tampered = true;
      return floor;
    }
    return Math.max(wall, floor);
  }

  /** True once a backwards jump has been seen. The UI should say so. */
  get tampered(): boolean {
    this.now();
    return this.#tampered;
  }

  get state(): ClockState | null {
    return this.#state;
  }
}
